import React, { Fragment, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import styled from "styled-components";
import isEmpty from "lodash/isEmpty";
import isFunction from "lodash/isFunction";

import { AtiraSpacePlanDefinition } from "../../../model/plan/Plan-definition";
import { AtiraSpacePlanPerksUsers } from "../../../model/plan/types/AtiraSpacePlanPerksUsers.enum";
import { AtiraSpacePlanType } from "../../../model/plan/types/AtiraSpacePlanType.enum";
import { LemonSubscriptionStatus } from "../../../model/plan/types/LemonSubscriptionStatus.enum";
import { ThresholdKinds } from "../../../model/threshold/types/ThresholdKinds.enum";
import { orderedPerksKeys, toUSD } from "../../../utils/pricingUtils";
import { Tooltip } from "../../../utils/local-antd";
import { Text, FontSize } from "../../../components/Text";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { Flex } from "../../../components/Flex";
import { Button, ButtonProps } from "../../../components/Button";
import { InputWithDigitsArrows } from "../../../components/InputWithDigitsArrows";
import { useAppSelector } from "../../../redux/store";
import { paymentSliceSelectors } from "../../../redux/payment/payment.selector";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { useTheme, ThemeKeys } from "../../../ThemeContext";
import { Images } from "../../../assets";
import { Spacing } from "../../../theme/Spacing";
import { Rounded } from "../../../theme/Rounded";
import { Shadow } from "../../../theme/Shadow";
import { Breakpoints } from "../../../theme/Breakpoints";
import { faCalendarCheck } from "@fortawesome/free-regular-svg-icons/faCalendarCheck";
import { faRotateLeft } from "@fortawesome/free-solid-svg-icons/faRotateLeft";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { faCircleUp } from "@fortawesome/free-solid-svg-icons/faCircleUp";
import { faStar } from "@fortawesome/free-solid-svg-icons/faStar";

enum NoticeModalType {
  CANCEL = "CANCEL",
  RESUME = "RESUME",
}

const logoByTheme: Record<ThemeKeys, string> = {
  [ThemeKeys.PURPLE]: Images.AtiraSpaceLogoIconPurple,
  [ThemeKeys.SLATE]: Images.AtiraSpaceLogoIconSlate,
  [ThemeKeys.YELLOW]: Images.AtiraSpaceLogoIconYellow,
  [ThemeKeys.TERRA]: Images.AtiraSpaceLogoIconTerra,
  [ThemeKeys.FOREST]: Images.AtiraSpaceLogoIconForest,
  [ThemeKeys.SCARELT]: Images.AtiraSpaceLogoIconScarlet,
  [ThemeKeys.OCEAN]: Images.AtiraSpaceLogoIconOcean,
  [ThemeKeys.MINT]: Images.AtiraSpaceLogoIconMint,
};

const TableWrapper = styled.div`
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  border-radius: ${Rounded.xl};
  box-shadow: ${Shadow.MD};
`;

const Grid = styled.div<{ $columns: number }>`
  display: grid;
  /* Definite lengths: with width: max-content an fr track is sized by the
     widest cell in the table, which inflates every column on mobile. The label
     column is wide on purpose: the table already scrolls sideways, so it can
     take room and keep labels to a line or two instead of a tall stack. */
  grid-template-columns: min(65vw, 15rem) repeat(
      ${({ $columns }) => $columns},
      min(85vw, 18rem)
    );
  width: max-content;
  overflow: hidden;
  border-radius: ${Rounded.xl};

  @media (min-width: ${Breakpoints.DESKTOP}) {
    width: 100%;
    grid-template-columns: minmax(11rem, 1.3fr) repeat(
        ${({ $columns }) => $columns},
        minmax(12rem, 1fr)
      );
  }
`;

const Cell = styled(Flex)<{ $isPopular?: boolean }>`
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: ${Spacing.xs};
  padding: ${Spacing.m};
  text-align: center;
  background-color: ${({ theme, $isPopular }) =>
    $isPopular ? theme.main : theme.white};
  border-top: 1px solid
    ${({ theme, $isPopular }) => ($isPopular ? `${theme.white}26` : theme.sub)};

  p {
    color: ${({ theme, $isPopular }) =>
      $isPopular ? theme.white : theme.textColor};
  }
`;

const LabelCell = styled(Flex)`
  align-items: center;
  gap: ${Spacing.s};
  padding: ${Spacing.m};
  background-color: ${({ theme }) => theme.lightSub};
  border-inline-start: 4px solid ${({ theme }) => theme.main};
  border-inline-end: 1px solid ${({ theme }) => theme.darkSub};
  border-top: 1px solid ${({ theme }) => theme.sub};
`;

const HeaderCell = styled(Cell)`
  position: relative;
  justify-content: flex-start;
  padding: ${Spacing.l};
  padding-top: calc(${Spacing.l} + 0.75rem);
  border-top: none;
`;

const LogoCell = styled(Flex)`
  align-items: center;
  justify-content: center;
  padding: ${Spacing.m};
  background-color: ${({ theme }) => theme.lightSub};
  border-inline-start: 4px solid ${({ theme }) => theme.main};
  border-inline-end: 1px solid ${({ theme }) => theme.darkSub};

  img {
    width: 5rem;
    height: 5rem;
    object-fit: contain;

    @media (min-width: ${Breakpoints.DESKTOP}) {
      width: 7rem;
      height: 7rem;
    }
  }
`;

const MutedNote = styled(Flex)`
  align-items: center;
  gap: ${Spacing.xs};
  margin-top: ${Spacing.s};
  opacity: 0.65;
`;

const SeatsSummary = styled(Flex)<{ $isPopular?: boolean }>`
  align-items: center;
  border: 1px solid
    ${({ theme, $isPopular }) => `${$isPopular ? theme.white : theme.main}66`};
  border-radius: ${Rounded.pill};

  > p {
    padding: ${Spacing.xs} ${Spacing.m};
  }

  > p + p {
    border-inline-start: 1px solid
      ${({ theme, $isPopular }) => `${$isPopular ? theme.white : theme.main}66`};
  }
`;

const CheckBadge = styled.span<{ $onDark?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: ${Rounded.circle};
  background-color: ${({ theme, $onDark }) =>
    $onDark ? theme.white : theme.success};
`;

/* "Most Popular" sits in its own row above the headers, so it never overlaps the
   header text and the plan names stay aligned across columns. */
const PopularTab = styled(Flex)`
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.s} ${Spacing.m};
  background-color: ${({ theme }) => theme.main};
  border-bottom: 1px solid ${({ theme }) => `${theme.white}26`};
`;

const TabSpacer = styled.div`
  background-color: ${({ theme }) => theme.white};
`;

const LabelTabSpacer = styled(TabSpacer)`
  background-color: ${({ theme }) => theme.lightSub};
  border-inline-end: 1px solid ${({ theme }) => theme.darkSub};
`;

const CurrentMark = styled(Flex)<{ $isPopular?: boolean }>`
  position: absolute;
  top: 0.5rem;
  inset-inline-start: 0.5rem;
  background: ${({ theme, $isPopular }) =>
    $isPopular ? theme.white : theme.sub};
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.pill};
  font-weight: bold;
  font-size: ${FontSize.xs};
  align-items: center;
  gap: ${Spacing.xs};
  color: ${({ theme }) => theme.main} !important;
`;

const CallToActionButton = styled(Button)<{ $isPopular?: boolean }>`
  width: 12rem;
  max-width: 100%;
  border: 1px solid
    ${({ theme, $isPopular }) => ($isPopular ? theme.white : theme.main)};
`;

const TooltipSpan = styled.span`
  margin-inline-start: ${Spacing.xs};
  vertical-align: middle;
`;

// Mirrors the "automation" rows of the public comparison table.
const includedFeatures: { key: string; planTypes: AtiraSpacePlanType[] }[] = [
  {
    key: "fahmy",
    planTypes: [
      AtiraSpacePlanType.PLAN_0,
      AtiraSpacePlanType.PLAN_1,
      AtiraSpacePlanType.PLAN_2,
    ],
  },
  {
    key: "whatsapp",
    planTypes: [AtiraSpacePlanType.PLAN_1, AtiraSpacePlanType.PLAN_2],
  },
  { key: "aiAgent", planTypes: [AtiraSpacePlanType.PLAN_2] },
];

type Props = {
  plans: AtiraSpacePlanDefinition[];
  onCreateCheckout: (variantId: string, seats: number) => void;
  setMode: (mode: NoticeModalType) => void;
  buttonsDisabled: boolean;
  planId?: string;
};

export const PrivatePlansTable: React.FC<Props> = ({
  plans,
  onCreateCheckout,
  setMode,
  buttonsDisabled,
  planId,
}) => {
  const { t } = useTranslation();
  const { selectedTheme } = useTheme();

  // plan.perks are the per-seat base quantities (USERS is always 1 there), and a
  // subscription's real perks are base × seats. The smallest subscription you can
  // buy (1 / 3 / 5 users) is the same rule the checkout endpoint enforces.
  const getPlanMinSeats = (plan: AtiraSpacePlanDefinition) =>
    AtiraSpacePlanPerksUsers[plan.type];

  const extraGlobalPerks: Record<AtiraSpacePlanType, string> = {
    [AtiraSpacePlanType.PLAN_0]: t("plan.support.plan_0"),
    [AtiraSpacePlanType.PLAN_1]: t("plan.support.plan_1"),
    [AtiraSpacePlanType.PLAN_2]: t("plan.support.plan_2"),
  };

  const [seatsByPlan, setSeatsByPlan] = useState<Record<string, number>>(() =>
    Object.fromEntries(plans.map((plan) => [plan._id, getPlanMinSeats(plan)])),
  );

  const checkoutLoading = useAppSelector(
    paymentSliceSelectors.selectCurrentCheckoutURLLoading,
  );
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  if (!plans?.length) {
    return null;
  }

  const logo = logoByTheme[selectedTheme] ?? Images.AtiraSpaceLogoIconPurple;

  const setSeats = (planId: string, next: number) => {
    setSeatsByPlan((prev) => ({ ...prev, [planId]: next }));
  };

  const isUserOnThisPlan = (plan: AtiraSpacePlanDefinition) =>
    user?.plan?.type === plan.type;

  // Seats the user actually has (their USERS threshold is the fallback for
  // accounts without a stored seat count).
  const getUserSeats = (plan: AtiraSpacePlanDefinition) =>
    user?.plan?.seats ??
    user?.threshold?.thresholds?.[ThresholdKinds.USERS] ??
    getPlanMinSeats(plan);

  const getSeats = (plan: AtiraSpacePlanDefinition) =>
    isUserOnThisPlan(plan)
      ? getUserSeats(plan)
      : seatsByPlan[plan._id] ?? getPlanMinSeats(plan);

  const formatNumber = (value: number) =>
    new Intl.NumberFormat("en-IN", { maximumSignificantDigits: 3 }).format(
      value,
    );

  function getNumericValue(
    plan: AtiraSpacePlanDefinition,
    key: string,
    value: number,
  ) {
    if (isUserOnThisPlan(plan)) {
      return value;
    }

    const seats = getSeats(plan);

    return key === ThresholdKinds.USERS ? seats : value * seats;
  }

  // The unit noun is chosen from the number actually shown (i18next plural
  // suffixes), so "1 User", "2 Pipelines" and the Arabic dual/few/many forms
  // are right for any value, including admin-granted thresholds.
  function getValueAndLabel(
    plan: AtiraSpacePlanDefinition,
    key: string,
    value: number,
  ) {
    if (key === ThresholdKinds.MOBILE_APP) {
      return t("plan.mobile_app.title");
    }

    const numeric = getNumericValue(plan, key, value);

    return `${formatNumber(numeric)} ${t(`plan.units.${key.toLowerCase()}`, { count: numeric })}`;
  }

  function getPerkValue(plan: AtiraSpacePlanDefinition, key: ThresholdKinds) {
    if (isUserOnThisPlan(plan)) {
      return user.threshold?.thresholds?.[key] || 0;
    }

    return plan.perks[key];
  }

  const scalingPerkKinds = [
    ThresholdKinds.ENTRIES,
    ThresholdKinds.FORMS,
    ThresholdKinds.KANBANS,
    ThresholdKinds.CUSTOM_FIELDS,
    ThresholdKinds.MODULES_DEFINITIONS,
  ];

  function getExtraPerkInfo(
    plan: AtiraSpacePlanDefinition,
    key: ThresholdKinds,
  ) {
    if (!isUserOnThisPlan(plan) || !scalingPerkKinds.includes(key)) {
      return null;
    }

    const actual = user.threshold?.thresholds?.[key];
    const expected = plan.perks[key] * getUserSeats(plan);

    if (actual === undefined || actual === null || actual <= expected) {
      return null;
    }

    return { actual: formatNumber(actual), expected: formatNumber(expected) };
  }

  const getActionButtonProps = (
    plan: AtiraSpacePlanDefinition,
  ): Partial<ButtonProps> => {
    const userPlan = user?.plan;
    const seats = seatsByPlan[plan._id] ?? getPlanMinSeats(plan);

    if (isEmpty(userPlan) || userPlan.type === AtiraSpacePlanType.PLAN_0) {
      return {
        title: t("subscription.plan.button.subscribe"),
        onClick: () => onCreateCheckout(plan.lemonVariantId, seats),
      };
    }

    const isCancelled =
      userPlan?.lemonPlanData?.status === LemonSubscriptionStatus.CANCELLED;
    const isCurrentPlan = userPlan?.type === plan.type;

    if (!isCurrentPlan) {
      return {};
    }

    if (isCancelled) {
      return {
        title: t("subscription.plan.button.resume"),
        onClick: () => setMode(NoticeModalType.RESUME),
      };
    }

    return {
      title: t("subscription.plan.button.cancel"),
      onClick: () => setMode(NoticeModalType.CANCEL),
    };
  };

  return (
    <TableWrapper>
      <Grid $columns={plans.length}>
        <LabelTabSpacer aria-hidden />

        {plans.map((plan) =>
          plan.type === AtiraSpacePlanType.PLAN_1 ? (
            <PopularTab key={plan._id}>
              <AtiraIcon icon={faStar} color="white" />
              <Text fontSize="xs" fontWeight={700} color="white">
                {t("plan.mostPopular")}
              </Text>
            </PopularTab>
          ) : (
            <TabSpacer key={plan._id} aria-hidden />
          ),
        )}

        <LogoCell>
          <img src={logo} alt="Atira Space" />
        </LogoCell>

        {plans.map((plan) => {
          const isPopular = plan.type === AtiraSpacePlanType.PLAN_1;
          const isFree = plan.type === AtiraSpacePlanType.PLAN_0;
          const seats = getSeats(plan);
          const showCurrentMark =
            !user?.plan?.freeTrial?.endsAt && plan.type === user?.plan?.type;

          return (
            <HeaderCell key={plan._id} $isPopular={isPopular}>
              {showCurrentMark ? (
                <CurrentMark $isPopular={isPopular}>
                  <span>{t("subscription.plan.current")}</span>
                  <AtiraIcon icon={faCheck} color="main" />
                </CurrentMark>
              ) : null}

              <Text
                fontSize="l"
                fontWeight={700}
                align="center"
                color={isPopular ? "white" : "main"}
              >
                {t(`plan.type.${plan.type.toLowerCase()}`)}
              </Text>

              <Text
                align="center"
                fontWeight={700}
                color={isPopular ? "white" : "textColor"}
              >
                {t(
                  `plan.paid.price.${isFree ? "free" : plan.type.toLowerCase()}`,
                )}
              </Text>

              <Flex alignItems="center" gap="xs">
                <AtiraIcon
                  icon={faCalendarCheck}
                  color={isPopular ? "white" : "main"}
                />
                <Text
                  fontSize="s"
                  align="center"
                  color={isPopular ? "white" : "textColor"}
                >
                  {t(`plan.paid.price.note${isFree ? ".free" : ""}`)}
                </Text>
              </Flex>

              <SeatsSummary $isPopular={isPopular}>
                <Text
                  fontSize="s"
                  fontWeight={700}
                  align="center"
                  color={isPopular ? "white" : "textColor"}
                >
                  {formatNumber(seats)}{" "}
                  {t("plan.units.users", { count: seats })}
                </Text>

                <Text
                  fontSize="s"
                  fontWeight={700}
                  align="center"
                  direction="ltr"
                  color={isPopular ? "white" : "textColor"}
                >
                  {toUSD(plan.lemonData.price * seats)}
                </Text>
              </SeatsSummary>
            </HeaderCell>
          );
        })}

        <LabelCell />

        {plans.map((plan) => {
          const isPopular = plan.type === AtiraSpacePlanType.PLAN_1;
          const showSeatsInput =
            plan.type !== AtiraSpacePlanType.PLAN_0 &&
            user?.plan?.type === AtiraSpacePlanType.PLAN_0;

          return (
            <Cell key={plan._id} $isPopular={isPopular}>
              {showSeatsInput ? (
                <InputWithDigitsArrows
                  title={t("subscription.plan.seats")}
                  info={t("subscription.plan.seats.info")}
                  mode="spinner"
                  infoColor={isPopular ? "white" : undefined}
                  titleColor={isPopular ? "white" : "main"}
                  min={getPlanMinSeats(plan)}
                  required
                  value={seatsByPlan[plan._id] ?? getPlanMinSeats(plan)}
                  onChange={(value) => setSeats(plan._id, Number(value))}
                />
              ) : null}
            </Cell>
          );
        })}

        {orderedPerksKeys.map((key) => (
          <Fragment key={key}>
            <LabelCell>
              <Text fontWeight={700} color="main">
                {t(`plan.${key.toLowerCase()}.title`)}
              </Text>
              {key === ThresholdKinds.ENTRIES ? (
                <Tooltip
                  title={
                    <Trans
                      i18nKey="plan.entries.tooltip"
                      components={[<br />]}
                    />
                  }
                >
                  <TooltipSpan>
                    <AtiraIcon icon={faInfoCircle} color="main" />
                  </TooltipSpan>
                </Tooltip>
              ) : null}
            </LabelCell>

            {plans.map((plan) => {
              const isPopular = plan.type === AtiraSpacePlanType.PLAN_1;
              const value = getPerkValue(plan, key);
              const extraInfo = getExtraPerkInfo(plan, key);

              return (
                <Cell key={plan._id} $isPopular={isPopular}>
                  <Flex alignItems="center" justifyContent="center" gap="xs">
                    <Text
                      align="center"
                      color={isPopular ? "white" : "textColor"}
                    >
                      {getValueAndLabel(plan, key, value as number)}
                    </Text>
                    {extraInfo ? (
                      <Tooltip
                        title={t("plan.extraGranted.tooltip", {
                          actual: extraInfo.actual,
                          expected: extraInfo.expected,
                        })}
                      >
                        <span>
                          <AtiraIcon icon={faCircleUp} color="success" />
                        </span>
                      </Tooltip>
                    ) : null}
                  </Flex>
                </Cell>
              );
            })}

            {key === ThresholdKinds.FORMS ? (
              <Fragment>
                <LabelCell>
                  <Text fontWeight={700} color="main">
                    {t("plan.customPerks.landingPage.plan_0")}
                  </Text>
                </LabelCell>

                {plans.map((plan) => {
                  const isPopular = plan.type === AtiraSpacePlanType.PLAN_1;
                  const numeric = getNumericValue(
                    plan,
                    ThresholdKinds.FORMS,
                    getPerkValue(plan, ThresholdKinds.FORMS) as number,
                  );

                  return (
                    <Cell key={plan._id} $isPopular={isPopular}>
                      <Text
                        align="center"
                        color={isPopular ? "white" : "textColor"}
                      >
                        {formatNumber(numeric)}{" "}
                        {t("plan.units.landing_pages", { count: numeric })}
                      </Text>
                    </Cell>
                  );
                })}
              </Fragment>
            ) : null}
          </Fragment>
        ))}

        {includedFeatures.map(({ key, planTypes }) => (
          <Fragment key={key}>
            <LabelCell>
              <Text fontWeight={700} color="main">
                {t(`plan.newFeatures.${key}.label`)}
              </Text>
              <Tooltip title={t(`plan.newFeatures.${key}.description`)}>
                <TooltipSpan>
                  <AtiraIcon icon={faInfoCircle} color="main" />
                </TooltipSpan>
              </Tooltip>
            </LabelCell>

            {plans.map((plan) => {
              const isPopular = plan.type === AtiraSpacePlanType.PLAN_1;

              return (
                <Cell key={plan._id} $isPopular={isPopular}>
                  {planTypes.includes(plan.type) ? (
                    <CheckBadge $onDark={isPopular}>
                      <AtiraIcon
                        icon={faCheck}
                        size="xs"
                        color={isPopular ? "success" : "white"}
                      />
                    </CheckBadge>
                  ) : null}
                </Cell>
              );
            })}
          </Fragment>
        ))}

        <LabelCell>
          <Text fontWeight={700} color="main">
            {t("plan.supportLabel")}
          </Text>
        </LabelCell>

        {plans.map((plan) => {
          const isPopular = plan.type === AtiraSpacePlanType.PLAN_1;

          return (
            <Cell key={plan._id} $isPopular={isPopular}>
              <Text align="center" color={isPopular ? "white" : "textColor"}>
                {extraGlobalPerks[plan.type]}
              </Text>
            </Cell>
          );
        })}

        <LabelCell />

        {plans.map((plan) => {
          const isPopular = plan.type === AtiraSpacePlanType.PLAN_1;
          const actionProps = getActionButtonProps(plan);
          const showButton =
            plan.type !== AtiraSpacePlanType.PLAN_0 &&
            isFunction(actionProps?.onClick);
          const canSubscribe =
            isEmpty(user?.plan) ||
            user.plan?.type === AtiraSpacePlanType.PLAN_0;

          return (
            <Cell key={plan._id} $isPopular={isPopular}>
              {showButton ? (
                <CallToActionButton
                  $isPopular={isPopular}
                  color="sub"
                  backgroundColor="main"
                  hover
                  loading={planId === plan.lemonVariantId && checkoutLoading}
                  disabled={buttonsDisabled}
                  {...actionProps}
                />
              ) : null}

              {showButton && canSubscribe ? (
                <MutedNote>
                  <AtiraIcon
                    icon={faRotateLeft}
                    color={isPopular ? "white" : "textColor"}
                  />
                  <Text fontSize="xs" align="center">
                    {t("plan.paid.money-back.note")}
                  </Text>
                </MutedNote>
              ) : null}
            </Cell>
          );
        })}
      </Grid>
    </TableWrapper>
  );
};
